// ============================================================================
// BOOKING PDF PREVIEW MODAL
// ============================================================================
// Fetches the confirmation PDF as a Blob, renders it inline in an <iframe>
// (browser-native PDF viewer), and offers a Download button. Replaces the
// previous straight-to-download flow so the cashier can review the document
// before sharing it with the guest.

import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { bookingService } from 'api/services';
import notificationService from 'services/notificationService';

interface BookingPdfPreviewModalProps {
  isOpen: boolean;
  bookingId: string | null;
  bookingNumber?: string | null;
  onClose: () => void;
}

const BookingPdfPreviewModal: React.FC<BookingPdfPreviewModalProps> = ({
  isOpen, bookingId, bookingNumber, onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    (async () => {
      if (!isOpen || !bookingId) return;
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      try {
        const blob = await bookingService.getConfirmationPdfBlob(bookingId);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoke = url;
        setBlobUrl(url);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [isOpen, bookingId]);

  const handleDownload = () => {
    if (!blobUrl || !bookingId) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = bookingNumber ? `Booking-${bookingNumber}.pdf` : `Booking-${bookingId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    notificationService.toast.success('Download started.');
  };

  const handlePrint = () => {
    if (!blobUrl) return;
    // Open the PDF in a new tab — most browsers auto-trigger their print
    // dialog when the user invokes File → Print from there. Safer than
    // trying to print() the iframe contents (cross-origin restrictions).
    window.open(blobUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop — NO onClick handler. The PDF preview is a deliberate review
          step before download/print; an accidental click outside the dialog
          shouldn't dismiss it. The user closes via the X / Close button. */}
      <div className="modal-backdrop" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Booking Confirmation Preview"
        className="modal-container"
        style={{
          // Large viewport-relative sizing so the PDF page is legible without
          // requiring user zoom. Capped to a sane maximum so it never overflows
          // a very wide monitor.
          width: 'min(96vw, 1400px)',
          height: 'min(94vh, 1100px)',
          maxWidth: '96vw',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Booking Confirmation Preview
            {bookingNumber && (
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                · {bookingNumber}
              </span>
            )}
          </h2>
          <button className="modal-close" onClick={onClose} title="Close preview">
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ flex: 1, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {loading && (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
            }}>
              <div style={{
                width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                Loading preview…
              </span>
            </div>
          )}

          {error && !loading && (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              padding: '2rem', textAlign: 'center',
            }}>
              <IconSystem.alert size={28} />
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Could not load preview</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{error}</div>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <iframe
              src={`${blobUrl}#toolbar=1&navpanes=0`}
              title="Booking Confirmation PDF"
              style={{ flex: 1, width: '100%', height: '100%', border: 'none', minHeight: 0 }}
            />
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button
            className="btn btn-secondary"
            onClick={handlePrint}
            disabled={loading || !blobUrl || !!error}
            title="Open in a new tab — use the browser's Print option from there"
          >
            <IconSystem.print size={16} />
            Open / Print
          </button>
          <button
            className="btn btn-primary"
            onClick={handleDownload}
            disabled={loading || !blobUrl || !!error}
          >
            <IconSystem.download size={16} />
            Download PDF
          </button>
        </div>
      </div>
    </>
  );
};

export default BookingPdfPreviewModal;
